<template>
  <section
    class="job-tree-question-node"
    :class="nodeClass"
  >
    <header class="job-question-header">
      <div class="job-question-title">
        {{ question.title }}<span v-if="question.required">*</span>
      </div>
      <div class="job-question-tools item-btn-group">
        <QuestionTypeBadge :question-type="question.type" />
        <template v-if="!disabled">
          <el-dropdown
            v-if="hasVisibilityOption && depth === 0"
            class="visibility-control"
            trigger="click"
            @command="$emit('visibility-change', question, $event)"
          >
            <button
              type="button"
              class="visibility-trigger"
              :aria-label="$t(`${i18nPrefix}.label.visibility`)"
            >
              <span>{{ $t(`${i18nPrefix}.visibilityType.${getVisibilityTypeName(question.visibility)}`) }}</span>
              <i class="el-icon-arrow-down" aria-hidden="true"></i>
            </button>
            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item
                v-for="option in visibilityOptionList"
                :key="option.value"
                :command="option.value"
              >
                {{ option.label }}
              </el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
          <span class="required-control">
            <span class="required-label">{{ $t('admin.company.questionnaireManagement.label.required') }}</span>
            <el-switch
              class="required-switch"
              :width="28"
              :value="Boolean(question.required)"
              :aria-label="$t('admin.company.questionnaireManagement.label.required')"
              @change="$emit('required-change', question)"
            />
          </span>
          <button
            v-if="depth === 0"
            type="button"
            class="tool-btn item-btn drag-btn"
            :aria-label="$t('common.label.sortOperate')"
          >
            <i class="iconfont icona-More1" aria-hidden="true"></i>
          </button>
          <button
            v-if="!rootOnlyQuestionActions || depth === 0"
            type="button"
            class="tool-btn item-btn"
            :aria-label="$t(`${i18nPrefix}.editor.question.title.edit`)"
            @click="$emit('edit', question)"
          >
            <i class="iconfont iconEditor" aria-hidden="true"></i>
          </button>
          <button
            v-if="!rootOnlyQuestionActions || depth === 0"
            type="button"
            class="tool-btn item-btn delete"
            :aria-label="$t(`${i18nPrefix}.message.deleteQuestionLabel`)"
            @click="$emit('delete', question)"
          >
            <i class="iconfont icondelete" aria-hidden="true"></i>
          </button>
        </template>
      </div>
    </header>

    <div v-if="hasOptions" class="job-options">
      <div
        v-for="(option, index) in question.options"
        :key="option.id || option._vid || index"
        class="job-option"
        :class="{ 'has-child': Boolean(resolvedChild(option)) }"
      >
        <div class="job-option-line" :class="{ knockout: isKnockoutOption(option) }">
          <div class="job-option-label">
            <span class="job-option-bullet" aria-hidden="true">-</span>
            <span class="job-option-text">{{ option.description }}</span>
          </div>
          <div v-if="showOptionActions(option)" class="job-option-actions">
            <el-button
              v-if="canOpenChild(option) && !hasChildReference(option)"
              type="text"
              class="option-action add-child-action"
              :aria-label="childActionLabel(option)"
              @click="$emit('open-child', question, option, index)"
            >
              <i class="el-icon-plus" aria-hidden="true"></i>
              <span>{{ childActionLabel(option) }}</span>
            </el-button>
            <button
              v-if="canOpenChild(option) && hasChildReference(option)"
              type="button"
              class="option-icon-action edit-child-relation"
              :aria-label="childActionLabel(option)"
              :title="childActionLabel(option)"
              @click="$emit('open-child', question, option, index)"
            >
              <i class="el-icon-edit-outline" aria-hidden="true"></i>
            </button>
            <button
              v-if="!disabled && hasChildReference(option)"
              type="button"
              class="option-icon-action remove-child-relation"
              :aria-label="removeChildLabel"
              :title="removeChildLabel"
              @click="$emit('remove-child', question, option, index)"
            >
              <i class="iconfont iconDelete" aria-hidden="true"></i>
            </button>
          </div>
        </div>
        <div v-if="resolvedChild(option)" class="job-option-child">
          <job-tree-question-node
            :question="resolvedChild(option)"
            :questions="questions"
            :disabled="disabled"
            :has-visibility-option="hasVisibilityOption"
            :visibility-option-list="visibilityOptionList"
            :child-question-parent-types="childQuestionParentTypes"
            :i18n-prefix="i18nPrefix"
            :root-only-question-actions="rootOnlyQuestionActions"
            :hide-existing-child-relation-actions="hideExistingChildRelationActions"
            :child-removal-mode="childRemovalMode"
            :depth="depth + 1"
            :max-depth="maxDepth"
            :visited-keys="currentVisitedKeys"
            @edit="$emit('edit', $event)"
            @delete="$emit('delete', $event)"
            @open-child="(...args) => $emit('open-child', ...args)"
            @remove-child="(...args) => $emit('remove-child', ...args)"
            @required-change="$emit('required-change', $event)"
            @visibility-change="(...args) => $emit('visibility-change', ...args)"
          />
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { HAS_OPTIONS_QUESTION_TYPES } from '@/constant/openQuestion'
import { MAX_TREE_DEPTH } from '@/utils/openQuestionTree'
import QuestionTypeBadge from './questionTypeBadge.vue'

function comparableKey (value) {
  return value === null || value === undefined || value === '' ? null : String(value)
}

export default {
  name: 'JobTreeQuestionNode',
  components: { QuestionTypeBadge },
  props: {
    question: {
      type: Object,
      required: true
    },
    questions: {
      type: Array,
      default: () => []
    },
    disabled: {
      type: Boolean,
      default: false
    },
    hasVisibilityOption: {
      type: Boolean,
      default: false
    },
    visibilityOptionList: {
      type: Array,
      default: () => []
    },
    childQuestionParentTypes: {
      type: Array,
      required: true
    },
    i18nPrefix: {
      type: String,
      required: true
    },
    depth: {
      type: Number,
      default: 0
    },
    maxDepth: {
      type: Number,
      default: MAX_TREE_DEPTH
    },
    visitedKeys: {
      type: Array,
      default: () => []
    },
    rootOnlyQuestionActions: {
      type: Boolean,
      default: false
    },
    hideExistingChildRelationActions: {
      type: Boolean,
      default: false
    },
    childRemovalMode: {
      type: String,
      default: 'detach-immediately'
    }
  },
  computed: {
    hasOptions () {
      return HAS_OPTIONS_QUESTION_TYPES.includes(this.question.type)
    },
    nodeClass () {
      return {
        root: this.depth === 0,
        'depth-one': this.depth === 1,
        'depth-deep': this.depth > 1
      }
    },
    questionKey () {
      return comparableKey(this.question && (this.question.id || this.question._vid))
    },
    normalizedVisitedKeys () {
      return this.visitedKeys.map(comparableKey).filter(Boolean)
    },
    currentVisitedKeys () {
      if (!this.questionKey || this.normalizedVisitedKeys.includes(this.questionKey)) {
        return this.normalizedVisitedKeys
      }
      return [...this.normalizedVisitedKeys, this.questionKey]
    },
    isCurrentNodeRepeated () {
      return !this.questionKey || this.normalizedVisitedKeys.includes(this.questionKey)
    },
    removeChildLabel () {
      const key = this.childRemovalMode === 'delete-subtree-on-save' ? 'message.deleteQuestionLabel' : 'btn.removeChild'
      return this.$t(`${this.i18nPrefix}.${key}`)
    }
  },
  methods: {
    questionIdentity (question) {
      return comparableKey(question && (question.id || question._vid))
    },
    questionMatchesKey (question, key) {
      return [question && question.id, question && question._vid]
        .map(comparableKey)
        .filter(Boolean)
        .includes(key)
    },
    optionChildKey (option) {
      if (!option) return null
      return comparableKey(
        option.child_question_id ||
        option.child_question_vid ||
        option.next_question_id ||
        (option.child_question && (option.child_question.id || option.child_question._vid))
      )
    },
    hasChildReference (option) {
      return Boolean(this.optionChildKey(option))
    },
    resolvedChild (option) {
      const childKey = this.optionChildKey(option)
      if (!childKey || this.isCurrentNodeRepeated || this.depth >= this.maxDepth) return null
      const matches = this.questions.filter(question => this.questionMatchesKey(question, childKey))
      if (matches.length !== 1 || this.currentVisitedKeys.includes(this.questionIdentity(matches[0]))) return null
      return matches[0]
    },
    isKnockoutOption (option) {
      return option && (option.is_knockout === true || option.is_knockout === 1 || option.is_knockout === '1')
    },
    canOpenChild (option) {
      return !this.disabled && !this.isCurrentNodeRepeated && !this.isKnockoutOption(option) &&
        this.childQuestionParentTypes.includes(this.question.type) && this.depth < this.maxDepth
    },
    showOptionActions (option) {
      if (this.hideExistingChildRelationActions && this.hasChildReference(option)) return false
      return this.canOpenChild(option) || (!this.disabled && this.hasChildReference(option))
    },
    childActionLabel (option) {
      const key = this.hasChildReference(option) ? 'changeChild' : 'setChild'
      return this.$t(`${this.i18nPrefix}.btn.${key}`)
    },
    getVisibilityTypeName (visibility) {
      const option = this.visibilityOptionList.find(item => item.value === visibility)
      return option ? option.typeName : 'IE'
    }
  }
}
</script>

<style lang="scss" scoped>
.job-tree-question-node {
  box-sizing: border-box;
  min-width: 0;

  &.root {
    margin-top: 12px;
    padding: 13px 10px 20px;
    background: #f1f1f1;
  }

  &.depth-one,
  &.depth-deep {
    margin: 10px 0 0 20px;
    background: #fff;
  }

  &.depth-one {
    border-radius: 4px 4px 0 0;
  }

  &.depth-deep {
    border: 1px solid #dcdfe6;
    border-radius: 4px;
  }
}

.job-question-header {
  display: flex;
  gap: 12px;
  align-items: center;
  min-width: 0;

  .depth-one > &,
  .depth-deep > & {
    min-height: 40px;
    padding: 10px 10px 10px 20px;
    border-bottom: 1px solid #dcdfe6;
    border-radius: 4px 4px 0 0;
    background: #fafafa;
  }
}

.job-question-title {
  flex: 1 1 240px;
  min-width: 0;
  color: #3b3b3b;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  overflow-wrap: anywhere;
  word-break: break-word;

  .depth-one > .job-question-header > &,
  .depth-deep > .job-question-header > & {
    color: #676767;
  }

  span {
    color: #f56c6c;
  }
}

.job-question-tools {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 10px;
  min-width: 0;
  margin-left: auto;
  color: #0096cc;
  white-space: nowrap;
}

.visibility-trigger,
.tool-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  padding: 0;
  border: 0;
  background: transparent;
  color: #0096cc;
  cursor: pointer;
}

.visibility-trigger {
  gap: 4px;
  min-height: 18px;
  font-size: 12px;
}

.required-control {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.required-label {
  color: #0096cc;
  font-size: 10px;
  font-weight: 400;
  line-height: 10px;
}

.required-switch {
  height: 14px;
  line-height: 14px;
}

::v-deep .required-switch .el-switch__core {
  width: 28px !important;
  height: 14px;
  border-radius: 7px;
}

::v-deep .required-switch .el-switch__core::after {
  top: 0.4px;
  left: 1px;
  width: 11.2px;
  height: 11.2px;
}

::v-deep .required-switch.is-checked .el-switch__core::after {
  left: 100%;
  margin-left: -12.2px;
}

.tool-btn {
  width: 18px;
  height: 18px;
  font-size: 18px;

  i {
    font-size: 12px;
  }

  &.drag-btn {
    cursor: grab;
  }

  &.delete {
    color: #0096cc;
  }
}

.job-options {
  display: flex;
  flex-direction: column;
  gap: 0;
  min-width: 0;

  .root > & {
    padding: 5px 15px 0;
  }

  .depth-one > &,
  .depth-deep > & {
    gap: 10px;
    padding: 9px 10px 10px 20px;
  }
}

.job-option {
  --branch-marker-width: 10px;
  --branch-marker-center-x: 5px;
  --branch-marker-center-y: 24px;
  --branch-child-indent: 20px;
  --branch-child-gap: 10px;
  --branch-child-anchor-y: 10px;
  min-width: 0;
}

.job-option-line {
  position: relative;
  display: flex;
  align-items: center;
  box-sizing: border-box;
  gap: 10px;
  min-height: 26px;
  padding: 3px 0;
  color: #676767;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;

  &.knockout {
    color: #f56c6c;
  }

  .has-child > &::before {
    position: absolute;
    top: var(--branch-marker-center-y);
    bottom: 0;
    left: var(--branch-marker-center-x);
    border-left: 1px solid #dcdfe6;
    content: '';
  }
}

.job-option-label {
  display: flex;
  flex: 1 1 auto;
  align-items: flex-start;
  gap: 10px;
  min-width: 0;
}

.job-option-bullet {
  flex: 0 0 var(--branch-marker-width);
  width: var(--branch-marker-width);
  text-align: center;
}

.job-option-child {
  position: relative;
  padding-top: var(--branch-child-gap);
  padding-left: var(--branch-child-indent);

  &::before {
    position: absolute;
    top: 0;
    left: var(--branch-marker-center-x);
    width: calc(var(--branch-child-indent) - var(--branch-marker-center-x));
    height: calc(var(--branch-child-gap) + var(--branch-child-anchor-y));
    border-bottom: 1px solid #dcdfe6;
    border-left: 1px solid #dcdfe6;
    content: '';
  }

  > .job-tree-question-node {
    margin: 0;
  }
}

.job-option-text {
  min-width: 0;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.job-option-actions {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 10px;
  margin-left: auto;
  white-space: nowrap;
}

::v-deep .option-action.el-button {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-height: 26px;
  margin: 0;
  padding: 0;
  color: #0096cc;
  font-size: 12px;
  font-weight: 400;
  line-height: 17px;

  i {
    font-size: 14px;
  }
}

.option-icon-action {
  display: inline-flex;
  flex: 0 0 18px;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  margin: 0;
  padding: 0;
  border: 0;
  background: transparent;
  color: #0096cc;
  cursor: pointer;

  i {
    font-size: 18px;
  }
}

@media (max-width: 1200px) {
  .job-question-header,
  .job-question-tools {
    flex-wrap: wrap;
  }

  .job-option-line {
    align-items: flex-start;
  }
}
</style>
